import {
    BillingPeriodEnum,
    CardTransactionTypeEnum,
    CommentItem,
    Company,
    CostCenter,
    CreditorInterface,
    CurrencyEnum,
    Employee,
    Expense,
    ExpenseKindEnum,
    ExpenseSplit,
    ExpenseStatusEnum,
    ExportFormatEnum,
    FileUploadDuplicateData,
    FinApiPayment,
    FinApiPaymentStatusEnum,
    Hospitality,
    InvoiceInterface,
    InvoiceSplit,
    ItemSplit,
    Mileage,
    PerDiem,
    PerDiemDestination,
    PriceIntervalEnum,
    RolesEnum,
    SplitTypeEnum,
    SubscriptionTypeEnum,
    TabTypeEnum,
    Tax,
    Transaction,
    TransactionTypeEnum,
    User,
    UserRule,
    Vendor,
    VendorTypeEnum,
    WeavrAuthorizationTypesEnum,
    WeavrSettlementTypesEnum,
    Workflow,
} from "@finway-group/shared/lib/models"
import { AutoGeneratedCommentsEnum } from "@finway-group/shared/lib/models/comment/autoGeneratedComments.enum"
import { ExpensePaymentFlowInformationEnum } from "@finway-group/shared/lib/models/expense/expensePaymentFlowInformation.enum"
import { ExpensePaymentOptionEnum } from "@finway-group/shared/lib/models/expense/expensePaymentOption.enum"
import { TransactionStatusEnum } from "@finway-group/shared/lib/models/transaction/transactionStatus.enum"
import { GetEmployeeById } from "@finway-group/shared/lib/models/user/employee.model"
import { isBuCodeDATEVCompliant } from "@finway-group/shared/lib/utils/datevValidation"
import { Modal } from "antd"
import dinero from "dinero.js"
import httpStatus from "http-status"
import FileDownload from "js-file-download"
import moment from "moment"

import { FINWAY_ADMIN_EMAIL, NO_TAX_RATE } from "Shared/config/consts"
import { useActiveTab } from "Shared/hooks/expense.hooks"
import i18n from "Shared/locales/i18n"
import { BankService, CurrencyService, EmployeeService, ExpenseService, FileService, NotificationService, UserService } from "Shared/services"
import DialogService from "Shared/services/dialog.service"
import { NotificationTypeEnum } from "Shared/services/notification.service"
import store from "Shared/store"
import {
    approveExpense,
    deleteExpense,
    fetchDashboardExpensesCounts,
    rejectExpense,
    reviewExpense,
    reviewMultipleExpenses,
    sendReminder,
    setExpenseDone,
    setExpensePaid,
    setMultipleExpensesAsDone,
    setMultipleExpensesAsPaid,
    setShouldRefetchExpenses,
    updateExpense,
} from "Shared/store/actions/expense/expenseActions"
import { refetchExpenseTables } from "Shared/store/actions/tables/tableActions"
import { TablesEnum } from "Shared/store/reducers/tableConfigReducer"
import {
    copyDeeply,
    formatZipFileName,
    getBufferFromEncoding,
    getTax,
    isArrayBuffer,
    isEmptyObject,
    isNotSet,
    parseMultipartBody,
    roundNumberTo2Decimals,
} from "Shared/utils/helpers"

import { getActiveWorkflowRule, getApproverForRule } from "../workflows/workflow.utils"
import { NOT_SET_VALUE } from "./expenseDetails/expenseSplitCells/cell.utils"

export const sortExpenses = (expenses: Expense[], type: "ASC" | "DESC" = "DESC") => {
    return expenses.sort((expA, expB) => {
        if (type === "ASC") {
            return new Date(expA.updatedAt).getTime() - new Date(expB.updatedAt).getTime()
        }

        return new Date(expB.updatedAt).getTime() - new Date(expA.updatedAt).getTime()
    })
}

export const needsDocs = (expense: Expense) => expense.status === ExpenseStatusEnum.DOCS_NEEDED

export const isMyPendingExpense = (userInfo: User, expense: Expense) => isPendingExpense(expense) && expense.requestedBy === userInfo.id

export const isPendingExpense = (expense: Expense) => expense.status === ExpenseStatusEnum.PURCHASE_PENDING || expense.status === ExpenseStatusEnum.INVOICE_PENDING

export const canApprove = (userInfo: User, expense: Expense) =>
    (expense.status === ExpenseStatusEnum.PURCHASE_PENDING || expense.status === ExpenseStatusEnum.INVOICE_PENDING) && expense.requestedBy !== userInfo.id

export const getPurchaseExpensesToApprove = (userInfo: User, expenses: Expense[]) =>
    getExpensesToApprove(userInfo, expenses).filter((exp) => exp.status === ExpenseStatusEnum.PURCHASE_PENDING)

export const getInvoiceExpensesToApprove = (userInfo: User, expenses: Expense[]) =>
    getExpensesToApprove(userInfo, expenses).filter((exp) => exp.status === ExpenseStatusEnum.INVOICE_PENDING)

export const getExpensesDocsNeeded = (expenses: Expense[]) => sortExpenses(expenses.filter((exp) => needsDocs(exp)))

export const getPendingExpenses = (userInfo: User, expenses: Expense[]) => sortExpenses(expenses.filter((exp) => isMyPendingExpense(userInfo, exp)))

export const getAllApprovedExpenses = (expenses: Expense[]) =>
    sortExpenses(expenses.filter((exp) => exp.status !== ExpenseStatusEnum.PURCHASE_PENDING && exp.status !== ExpenseStatusEnum.INVOICE_PENDING))

export const determinePossibleApproversForExpense = (
    expense: Expense,
    employees: Employee[],
    costCenters: CostCenter[],
    loggedInUser: Employee,
    workflows: any[],
    isPreApprovedSubscription: boolean,
    filterRequester = true
) => {
    const requestedBy = GetEmployeeById(employees, expense.requestedBy)
    const expensePrice = loggedInUser.settings.showGrossAmount ? expense.totalGrossPrice : expense.totalNetPrice

    // filter requester is by default set to true for expenses approval, and set to false for expense tagging list
    let filteredEmployees = employees.filter(
        ({ activeCompanyProfile: { role, deleted }, id }) => !deleted && (role === RolesEnum.ADMIN || role === RolesEnum.APPROVER || (!filterRequester && id === requestedBy?.id))
    )

    // if not in the requester's limits and if the request's payment option is smart card, remove auto approver
    if (requestedBy) {
        // do allow it however if the card is already created
        const isAutoApproverAllowedForCardRequest = expense.card !== undefined

        const limit = getAutoApprovalLimit(employees, requestedBy, expense.currency)
        if (!limit || limit < expensePrice || (expense?.paymentOption === ExpensePaymentOptionEnum.SMART_CARD && !isAutoApproverAllowedForCardRequest)) {
            filteredEmployees = filteredEmployees.filter(({ email }) => email !== FINWAY_ADMIN_EMAIL)
        }
    }

    // also add the superior to the list
    if (requestedBy?.activeCompanyProfile.superior) {
        const superior = GetEmployeeById(employees, requestedBy.activeCompanyProfile.superior)
        if (superior && !filteredEmployees.includes(superior)) filteredEmployees.push(superior)
    }

    // for preapproved, also add the CC responsible to the list
    const costCenter = costCenters.find((costCenter: CostCenter) => costCenter._id === expense.costCenter)
    if (costCenter && !filteredEmployees.find((emp) => emp.id === costCenter.responsibleUser)) {
        // @todo this below looks weird
        const responsible = GetEmployeeById(employees, costCenter.responsibleUser)
        if (responsible) {
            filteredEmployees.push(responsible)
        }
    }

    // requester should not be a possible approver himself
    if (requestedBy && filterRequester) filteredEmployees = filteredEmployees.filter((employee) => employee?.id !== requestedBy.id)

    // lastly remove all that are listed in the limits and we already exceeded their approval limit
    filteredEmployees = filteredEmployees.filter((employee: Employee) => {
        if (!employee) return false
        if (employee.activeCompanyProfile.isAdmin && employee.email !== FINWAY_ADMIN_EMAIL) return true

        const foundInApproval = requestedBy?.activeCompanyProfile.approvals?.find((approval) => approval.approver === employee.id)
        if (foundInApproval) {
            const limit =
                foundInApproval.currency === expense.currency ? foundInApproval.limit : CurrencyService.convert(foundInApproval.limit, foundInApproval.currency, expense.currency)
            return limit >= expensePrice
        }
        return true
    })

    if (expense.workflow) {
        const { activeRule } = getActiveWorkflowRule(expense, workflows)
        if (activeRule) {
            const workflowApprover = GetEmployeeById(employees, getApproverForRule(activeRule.approverData, expense))
            if (workflowApprover && !filteredEmployees.includes(workflowApprover)) {
                filteredEmployees.push(workflowApprover)
            }
            if (activeRule.secondApprover) {
                const secondWorkflowApprover = GetEmployeeById(employees, getApproverForRule(activeRule.secondApprover, expense))
                if (secondWorkflowApprover && !filteredEmployees.includes(secondWorkflowApprover)) {
                    filteredEmployees.push(secondWorkflowApprover)
                }
            }
        }
    }

    // if pre-approved sub and card doesn't have to be created, enable auto-approver
    if (
        isPreApprovedSubscription &&
        loggedInUser.activeCompanyProfile.isAdmin &&
        !filteredEmployees.find(({ email }) => email === FINWAY_ADMIN_EMAIL) &&
        (expense.card || expense.paymentOption !== ExpensePaymentOptionEnum.SMART_CARD)
    ) {
        const autoApprover = employees.find(({ email }) => email === FINWAY_ADMIN_EMAIL)
        autoApprover && filteredEmployees.push(autoApprover)
    }

    // if the current value is not contained, we gotta add that too to avoid weird IDs to show
    if (!filteredEmployees.find(({ id }) => id === expense.approvalNeededBy)) {
        const currentApprover = employees.find((emp) => emp.id === expense.approvalNeededBy)
        currentApprover && filteredEmployees.push(currentApprover)
    }

    return filteredEmployees
}

const getAutoApprovalLimit = (employees: Employee[], requestedBy: Employee, expenseCurrency: CurrencyEnum) => {
    const autoApprover = employees.find(({ email }) => email === FINWAY_ADMIN_EMAIL)
    const autoApprovalLimit = requestedBy?.activeCompanyProfile.approvals?.find(({ approver }) => approver === autoApprover?.id)
    // prettier-ignore
    const limitValue = autoApprovalLimit && (autoApprovalLimit.currency === expenseCurrency ? autoApprovalLimit.limit : CurrencyService.convert(autoApprovalLimit.limit, autoApprovalLimit.currency, expenseCurrency));

    return limitValue
}

export const determineApprover = (user: Employee, amount: number, amountCurrency: CurrencyEnum): string | undefined => {
    // ideally, the user has an approver set
    if (user.activeCompanyProfile.approvals && user.activeCompanyProfile.approvals.length > 0) {
        let approver = undefined
        user.activeCompanyProfile
            .approvals!.sort((a, b) => (a.limit > b.limit ? 1 : -1))
            .some((approval) => {
                const limit = approval.currency === amountCurrency ? approval.limit : CurrencyService.convert(approval.limit, approval.currency, amountCurrency)

                if (limit >= amount) {
                    // found approver
                    approver = approval.approver
                    return true
                }
                return false
            })

        if (approver) return approver
    }

    // if not, the superior will be the approver
    if (user instanceof Employee && user.activeCompanyProfile?.superior) {
        // as long as he wasn't excluded by the limit checking above
        if (!user.activeCompanyProfile?.approvals?.find(({ approver }) => approver === user.activeCompanyProfile?.superior)) return user.activeCompanyProfile?.superior
    }

    return undefined
}

export const approveExpenseRequest = (expense: Expense) =>
    approveExpense(expense.id)(store.dispatch)
        .then(() => NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.approved.title`), i18n.t(`notification:request.approved.message`)))
        .catch((err) => {
            if (err === "cancelled") return
            NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.approved.title`))
            throw err
        })

export const rejectExpenseRequest = (expense: Expense, rejectionNote: string) =>
    rejectExpense(
        expense.id,
        rejectionNote
    )(store.dispatch)
        .then(() => NotificationService.send(NotificationTypeEnum.WARNING, i18n.t(`notification:request.rejected.title`), i18n.t(`notification:request.rejected.message`)))
        .catch((err) => {
            if (err === "cancelled") return
            NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.rejected.title`))
            throw err
        })

export const cancelExpenseRequest = (expense: Expense, shouldUseDeleteLabel = false, onCancel?: () => void) => {
    const { confirm } = Modal
    confirm({
        title: shouldUseDeleteLabel ? i18n.t("confirm:request.delete.title") : i18n.t("confirm:request.cancel.title"),
        type: "warning",
        content: shouldUseDeleteLabel ? i18n.t("confirm:request.delete.message") : i18n.t("confirm:request.cancel.message"),
        cancelText: shouldUseDeleteLabel ? i18n.t("confirm:request.delete.cancel") : i18n.t("confirm:request.cancel.cancel"),
        okText: shouldUseDeleteLabel ? i18n.t("confirm:request.delete.confirm") : i18n.t("confirm:request.cancel.confirm"),
        onOk() {
            return new Promise<void>((resolve, reject) => {
                deleteExpense(expense.id)(store.dispatch)
                    .then(() => {
                        NotificationService.send(NotificationTypeEnum.WARNING, i18n.t("notification:request.cancelled.title"), i18n.t("notification:request.cancelled.message"))
                        resolve()
                    })
                    .catch((err) => {
                        NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t("error:request.cancel.title"))
                        reject()
                    })
            })
                .then(() => onCancel && onCancel())
                .catch((err) => NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t("error:request.cancel.title")))
        },
        onCancel() {},
    })
}

export const markExpenseAsReviewed = async (expense: Expense, refetchExpenseTables = true) => {
    // confirm reporting only status
    const PAYMENT_FLOW_TO_CONFIRM = [ExpensePaymentFlowInformationEnum.ALREADY_PAID, ExpensePaymentFlowInformationEnum.ALREADY_APPROVED_AND_PAID]
    // prettier-ignore
    if (expense.paymentFlowInformation && PAYMENT_FLOW_TO_CONFIRM.includes(expense.paymentFlowInformation) && !(await DialogService.confirmPaymentFlowInformationAlreadyPaidWarningOnReview(expense.paymentFlowInformation))) return

    return reviewExpense(
        expense.id,
        refetchExpenseTables
    )(store.dispatch)
        .then(() => NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.reviewed.title`), i18n.t(`notification:request.reviewed.message`)))
        .catch((err) => {
            if (err === "cancelled") return
            NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.reviewed.title`))
            throw err
        })
}

export const payExpenses = (expenses: Expense[], accountId: string) => {
    return BankService.issuePayment(
        accountId,
        expenses.map((e: Expense) => e.id)
    )
        .then(() =>
            NotificationService.send(NotificationTypeEnum.INFO, i18n.t("notification:banking.payment_started.title"), i18n.t("notification:banking.payment_started.message"), 30)
        )
        .catch((err) => NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t("error:banking.error")))
}

export const markExpenseAsPaid = (expense: Expense, refetchExpenseTables = true) =>
    setExpensePaid(
        expense.id,
        refetchExpenseTables
    )(store.dispatch)
        .then(() => NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.paid.title`), i18n.t(`notification:request.paid.message`)))
        .catch((err) => {
            if (err === "cancelled") return
            NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.paid.title`))
            throw err
        })

export const markExpenseAsDone = (expense: Expense, refetchExpenseTables = true) =>
    setExpenseDone(
        expense.id,
        refetchExpenseTables
    )(store.dispatch)
        .then(() => NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.exported.title`), i18n.t(`notification:request.exported.message`)))
        .catch((err) => {
            if (err === "cancelled") return
            NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.exported.title`))
            throw err
        })

export const markMultipleExpensesAsReviewed = async (expenses: Expense[]) => {
    // confirm reporting only status
    const PAYMENT_FLOW_TO_CONFIRM = [ExpensePaymentFlowInformationEnum.ALREADY_PAID, ExpensePaymentFlowInformationEnum.ALREADY_APPROVED_AND_PAID]
    // prettier-ignore
    const paymentFlowSet = expenses.find((expense) => expense.paymentFlowInformation && PAYMENT_FLOW_TO_CONFIRM.includes(expense.paymentFlowInformation))
    if (paymentFlowSet && !(await DialogService.confirmPaymentFlowsOnBulkReview())) return

    try {
        const expenseIds = expenses.map(({ id }) => id)
        const filteredOutExpenseNumbers = await reviewMultipleExpenses(expenseIds)(store.dispatch)

        if (filteredOutExpenseNumbers.length) {
            // prettier-ignore
            NotificationService.send(NotificationTypeEnum.WARNING, i18n.t(`notification:request.reviewed_bulk_partial.title`), i18n.t(`notification:request.reviewed_bulk_partial.message`, { expenses: filteredOutExpenseNumbers }), 0)
        } else {
            NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.reviewed_bulk.title`), i18n.t(`notification:request.reviewed_bulk.message`))
        }
    } catch (err) {
        if (err === "cancelled") return
        NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.reviewed.title`))
        throw err
    }
}

export const markMultipleExpensesAsPaid = async (expenses: Expense[]) => {
    try {
        const expenseIds = expenses.map(({ id }) => id)
        const filteredOutExpenseNumbers = await setMultipleExpensesAsPaid(expenseIds)(store.dispatch)

        if (filteredOutExpenseNumbers.length) {
            // prettier-ignore
            NotificationService.send(NotificationTypeEnum.WARNING, i18n.t(`notification:request.paid_bulk_partial.title`), i18n.t(`notification:request.paid_bulk_partial.message`, { expenses: filteredOutExpenseNumbers }), 0)
        } else {
            NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.paid_bulk.title`), i18n.t(`notification:request.paid_bulk.message`))
        }
    } catch (err) {
        if (err === "cancelled") return
        NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.paid.title`))
        throw err
    }
}

export const markMultipleExpensesAsDone = async (expenses: Expense[]) => {
    try {
        const expenseIds = expenses.map(({ id }) => id)
        const filteredOutExpenseNumbers = await setMultipleExpensesAsDone(expenseIds)(store.dispatch)

        if (filteredOutExpenseNumbers.length) {
            // prettier-ignore
            NotificationService.send(NotificationTypeEnum.WARNING, i18n.t(`notification:request.exported_bulk_partial.title`), i18n.t(`notification:request.exported_bulk_partial.message`, { expenses: filteredOutExpenseNumbers.join(", ") }), 0)
        } else {
            NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.exported_bulk.title`), i18n.t(`notification:request.exported_bulk.message`))
        }
    } catch (err) {
        if (err === "cancelled") return
        NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.exported.title`))
        throw err
    }
}

export const getRejectionComment = (expense: Expense, defaultKindMessage: AutoGeneratedCommentsEnum): AutoGeneratedCommentsEnum => {
    if (expense.isReimbursement) {
        switch (expense.kind) {
            case ExpenseKindEnum.HOSPITALITY:
                return AutoGeneratedCommentsEnum.HOSPITALITY_REJECTED
            case ExpenseKindEnum.PER_DIEM:
                return AutoGeneratedCommentsEnum.PER_DIEM_REJECTED
            case ExpenseKindEnum.MILEAGE:
                return AutoGeneratedCommentsEnum.MILEAGE_REJECTED
            default:
                return AutoGeneratedCommentsEnum.STANDARD_REIMBURSEMENT_REJECTED
        }
    } else {
        return defaultKindMessage
    }
}

export const getInvoiceApprovalComment = (expense: Expense): AutoGeneratedCommentsEnum => {
    if (expense.isReimbursement) {
        switch (expense.kind) {
            case ExpenseKindEnum.PER_DIEM:
                return AutoGeneratedCommentsEnum.PER_DIEM_APPROVED
            case ExpenseKindEnum.MILEAGE:
                return AutoGeneratedCommentsEnum.MILEAGE_APPROVED
            case ExpenseKindEnum.HOSPITALITY:
                return AutoGeneratedCommentsEnum.HOSPITALITY_APPROVED
            default:
                return AutoGeneratedCommentsEnum.STANDARD_REIMBURSEMENT_APPROVED
        }
    } else {
        return AutoGeneratedCommentsEnum.INVOICE_REQUEST_APPROVED
    }
}

export const restoreExpense = (expensePara: Expense, currentUserId: string) => {
    const expense = copyDeeply(expensePara, Expense)
    expense.deleted = false
    expense.dateDeleted = undefined
    expense.comments.push(
        new CommentItem({
            postedBy: currentUserId,
            message: `request.restored`, // TODO
            datePosted: new Date(),
            isAutoGenerated: true,
        })
    )

    return applyExpenseUpdate(expense, "restored")
}

export const applyExpenseUpdate = (expense: Expense, notificationLabel: string, skipHandling = false, refetchExpenseTables = true) => {
    return updateExpense(
        expense.id,
        expense,
        refetchExpenseTables
    )(store.dispatch)
        .then(() => {
            if (!skipHandling)
                NotificationService.send(
                    NotificationTypeEnum.SUCCESS,
                    i18n.t(`notification:request.${notificationLabel}.title`),
                    i18n.t(`notification:request.${notificationLabel}.message`)
                )
        })
        .catch((err) => {
            if (err === "cancelled") return
            if (!skipHandling) NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.${notificationLabel}.title`))
            throw err
        })
}

export const sortDesc = (expenses: Expense[], by: string = "dateLastUpdated") =>
    expenses.sort((expA: { [key: string]: any }, expB: { [key: string]: any }) => new Date(expB[by]).getTime() - new Date(expA[by]).getTime())

export const isExpenseProcessed = (expense: Expense) => expense.status === ExpenseStatusEnum.APPROVED || expense.status === ExpenseStatusEnum.DECLINED

export const isExpenseStatusAtLeastApproved = (status: ExpenseStatusEnum) =>
    [ExpenseStatusEnum.APPROVED, ExpenseStatusEnum.REVIEWED, ExpenseStatusEnum.PAID, ExpenseStatusEnum.DONE].includes(status)

export const isExpenseStatusAtLeastDocsNeeded = (status: ExpenseStatusEnum) => status !== ExpenseStatusEnum.PURCHASE_PENDING

export const getBillingInMonths = (billingInterval: BillingPeriodEnum) =>
    billingInterval === BillingPeriodEnum.MONTHLY ? 1 : billingInterval === BillingPeriodEnum.QUARTERLY ? 3 : 12

export const getPriceIntervalForBillingPeriod = (billingPeriod: number) => {
    switch (billingPeriod) {
        case BillingPeriodEnum.QUARTERLY:
            return PriceIntervalEnum.QUARTERLY
        case BillingPeriodEnum.YEARLY:
            return PriceIntervalEnum.YEARLY
        default:
            return PriceIntervalEnum.MONTHLY
    }
}

export const sendEmailReminder = (userId: string, employees: Employee[], expense: Expense) => {
    // send out email confirmation
    return sendReminder(expense.id)(store.dispatch)
        .then(() => {
            if (expense.status === ExpenseStatusEnum.DOCS_NEEDED) {
                const requester = GetEmployeeById(employees, expense.requestedBy)
                if (!requester) {
                    NotificationService.send(NotificationTypeEnum.WARNING, "", i18n.t("notification:request.reminder_not_sent.message"))
                    return
                }
            } else {
                const approver = GetEmployeeById(employees, expense.approvalNeededBy)
                if (!approver) {
                    NotificationService.send(NotificationTypeEnum.WARNING, "", i18n.t("notification:request.reminder_not_sent.message"))
                    return
                }
            }
            NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t("notification:request.reminder_sent.title"), i18n.t("notification:request.reminder_sent.message"))
        })
        .catch((error: any) => {
            NotificationService.showErrorNotificationBasedOnResponseError(error, i18n.t("error:request.send_reminder.title"))
        })
}

export const getLabelForStatusUpdate = (status: ExpenseStatusEnum) => {
    switch (status) {
        case ExpenseStatusEnum.DOCS_NEEDED:
        case ExpenseStatusEnum.APPROVED:
            return "approved"
        case ExpenseStatusEnum.DECLINED:
            return "rejected"
        case ExpenseStatusEnum.REVIEWED:
            return "reviewed"
        case ExpenseStatusEnum.PAID:
            return "paid"
        case ExpenseStatusEnum.DONE:
            return "exported"
        default:
            throw new Error("Unexpected status")
    }
}

export const getStatusLabel = (status: ExpenseStatusEnum) => {
    switch (status) {
        case ExpenseStatusEnum.PURCHASE_PENDING:
            return i18n.t("label:purchase_pending")
        case ExpenseStatusEnum.DOCS_NEEDED:
            return i18n.t("label:documents_needed")
        case ExpenseStatusEnum.INVOICE_PENDING:
            return i18n.t("label:invoice_pending")
        case ExpenseStatusEnum.APPROVED:
            return i18n.t("label:in_review")
        case ExpenseStatusEnum.DECLINED:
            return i18n.t("label:declined")
        case ExpenseStatusEnum.REVIEWED:
            return i18n.t("label:in_payment")
        case ExpenseStatusEnum.PAID:
            return i18n.t("label:in_export")
        case ExpenseStatusEnum.DONE:
            return i18n.t("label:processed")
        default:
            return "n/a"
    }
}

export const getExpensesToApprove = (userInfo: User, expenses: Expense[]) => sortExpenses(expenses.filter((exp) => canApprove(userInfo, exp)))

export const isRequestActionDisabled = (user: Employee, vendors: Vendor[], costCenters: CostCenter[], employees: Employee[]) => {
    const filteredEmployees = employees.filter(
        (employeeToFilter) =>
            employeeToFilter.activeCompanyProfile &&
            !employeeToFilter.activeCompanyProfile.deleted &&
            ((employeeToFilter.activeCompanyProfile.role === RolesEnum.ADMIN && employeeToFilter.email !== FINWAY_ADMIN_EMAIL) ||
                (employeeToFilter.activeCompanyProfile.role === RolesEnum.APPROVER && employeeToFilter.activeCompanyProfile.team === user.activeCompanyProfile.team))
    )

    return vendors.length === 0 || costCenters.length === 0 || filteredEmployees.length === 0
}

// TODO: can we remove this after the migration?
export const convertToSortingString = (sortingCriteria: any[]) => {
    if (sortingCriteria.length === 0) return ""
    const sortingStrings = sortingCriteria.map((criteria: any, i) => {
        // antd understands "ascend and descend" and back end understands "asc and desc"
        let criteriaOrderAux = criteria.order
        if (criteria.order === "ascend") criteriaOrderAux = "asc"
        if (criteria.order === "descend") criteriaOrderAux = "desc"
        return `${criteriaOrderAux}(${criteria.field})`
    })
    return `&sortBy=${sortingStrings.join(",")}`
}

// antd understands "ascend and descend" and back end understands "asc and desc"
export const convertSortingToString = (sorting: any) => {
    if (sorting.order || sorting.field) return `&sortBy=${sorting.order === "ascend" ? "asc" : "desc"}(${sorting.field})`
    else return ""
}

export const isSubscriptionRenewingSoon = (date: Date) => {
    const in8Weeks = moment().add(8, "w")
    const currentDate = moment()
    return moment(date).isBetween(currentDate, in8Weeks, undefined, "[]")
}

export const getAutoCommentEnumForStatus = (status: ExpenseStatusEnum) => {
    switch (status) {
        case ExpenseStatusEnum.DOCS_NEEDED:
            return AutoGeneratedCommentsEnum.PURCHASE_REQUEST_APPROVED
        case ExpenseStatusEnum.APPROVED:
            return AutoGeneratedCommentsEnum.INVOICE_REQUEST_APPROVED
        case ExpenseStatusEnum.DECLINED:
            return AutoGeneratedCommentsEnum.REQUEST_REJECTED
        case ExpenseStatusEnum.REVIEWED:
            return AutoGeneratedCommentsEnum.REQUEST_REVIEWED
        case ExpenseStatusEnum.PAID:
            return AutoGeneratedCommentsEnum.REQUEST_PAID
        case ExpenseStatusEnum.DONE:
            return AutoGeneratedCommentsEnum.REQUEST_MARKED_AS_DONE
        default:
            return ""
    }
}

export const getSubscriptionPriceInterval = (expense: Expense) => {
    switch (expense.billingPeriod) {
        case BillingPeriodEnum.MONTHLY:
            return PriceIntervalEnum.MONTHLY
        case BillingPeriodEnum.QUARTERLY:
            return PriceIntervalEnum.QUARTERLY
        case BillingPeriodEnum.YEARLY:
            return PriceIntervalEnum.YEARLY
    }
}

export const confirmDatevWarning = (callback: any, multiple = false) => {
    Modal.confirm({
        title: i18n.t("confirm:request.datev_warning.title"),
        content: i18n.t(`confirm:request.datev_warning.message${multiple ? "_multiple" : ""}`),
        cancelText: i18n.t("confirm:request.datev_warning.cancel"),
        type: "warning",
        okText: i18n.t("confirm:request.datev_warning.confirm"),
        onOk() {
            callback()
        },
    })
}

export const getExpenseCards = (expenses: any[]) =>
    expenses
        .map((exp) => exp.card)
        .filter((card) => card)
        .join(",")

/**
 * Only for expenses that have transactionState or expenses that have looked up/populated the matchedTransactions
 */
export const summarizeExpenseTransactions = (expense: Expense) => {
    const transactionState = expense.transactionState
    const matchedTransactions = expense.matchedTransactions as Transaction[]

    if (!isNotSet(transactionState)) return transactionState

    if (matchedTransactions?.length > 0) {
        if ([TransactionTypeEnum.BANK_TRANSACTION, TransactionTypeEnum.CSV_TRANSACTION].includes(matchedTransactions[0].transactionType)) {
            return matchedTransactions[0].status
        }
        if (matchedTransactions[0].transactionType === TransactionTypeEnum.CARD_TRANSACTION) {
            const settlementTransactions = matchedTransactions.filter((mt) => Object.values(WeavrSettlementTypesEnum).includes(mt.cardData.cardTransactionType as any))

            // show pending when there's at least one pending
            if (matchedTransactions.some((mt) => mt.status === TransactionStatusEnum.PENDING)) return TransactionStatusEnum.PENDING

            // if 1 settlement succeeded, show completed
            if (settlementTransactions.some((mt) => mt.status === TransactionStatusEnum.COMPLETED)) return TransactionStatusEnum.COMPLETED

            // if there is none pending, no settlement and at least one failed that's not a reversal, show as failed
            if (matchedTransactions.some((mt) => mt.status === TransactionStatusEnum.FAILED && mt.cardData.cardTransactionType !== WeavrAuthorizationTypesEnum.ONLINE_REVERSE))
                return TransactionStatusEnum.FAILED
        }
    }
    return undefined
}

export const getPeriodPricesForSubscription = (expense: any, showGrossAmount: boolean) => {
    let monthlyPrice, quarterlyPrice, yearlyPrice
    switch (expense.billingPeriod) {
        case BillingPeriodEnum.MONTHLY:
            monthlyPrice = showGrossAmount ? expense.totalGrossPrice : expense.totalNetPrice
            quarterlyPrice = roundNumberTo2Decimals(monthlyPrice * 3)
            yearlyPrice = roundNumberTo2Decimals(monthlyPrice * 12)
            break
        case BillingPeriodEnum.QUARTERLY:
            quarterlyPrice = showGrossAmount ? expense.totalGrossPrice : expense.totalNetPrice
            monthlyPrice = roundNumberTo2Decimals(quarterlyPrice / 3)
            yearlyPrice = roundNumberTo2Decimals(quarterlyPrice * 4)
            break
        case BillingPeriodEnum.YEARLY:
            yearlyPrice = showGrossAmount ? expense.totalGrossPrice : expense.totalNetPrice
            monthlyPrice = roundNumberTo2Decimals(yearlyPrice / 12)
            quarterlyPrice = roundNumberTo2Decimals(yearlyPrice / 4)
    }
    return { monthlyPrice: Number(monthlyPrice), quarterlyPrice: Number(quarterlyPrice), yearlyPrice: Number(yearlyPrice) }
}

export const doTotalAndInlineAmountsMismatch = (expense: Expense, taxes: Tax[], isNew: boolean = true) => {
    // early out
    if (expense.kind !== ExpenseKindEnum.ONE_TIME_EXPENSE || expense.splits.length === 0 || expense.splitType === SplitTypeEnum.SPLIT) return false

    const totalNetPriceDiff = isExpenseAndTotalSplitPriceMatch(expense, PriceSourceEnum.NET_PRICE)

    const totalGrossPriceDiff = isExpenseAndTotalSplitPriceMatch(expense, PriceSourceEnum.GROSS_PRICE)

    // prettier-ignore
    const taxRateDiff = expense.taxRate === NO_TAX_RATE || (!isNew && !expense.taxRate)
        ? isExpenseAndTotalSplitPriceMatch(expense, PriceSourceEnum.TAX_PRICE)
        : getCommonTaxRateAcrossItems(expense, taxes) !== expense.taxRate

    return totalNetPriceDiff || totalGrossPriceDiff || taxRateDiff
}

enum PriceSourceEnum {
    NET_PRICE = "netPrice",
    TAX_PRICE = "taxPrice",
    GROSS_PRICE = "grossPrice",
}

export const isExpenseAndTotalSplitPriceMatch = (expense: Expense, priceSource: PriceSourceEnum) => {
    let expenseTotalPrice: number

    switch (priceSource) {
        case PriceSourceEnum.NET_PRICE:
            expenseTotalPrice = expense.totalNetPrice
            break
        case PriceSourceEnum.GROSS_PRICE:
            expenseTotalPrice = expense.totalGrossPrice
            break
        case PriceSourceEnum.TAX_PRICE:
            expenseTotalPrice = expense.totalTaxPrice
            break
    }

    return (
        roundNumberTo2Decimals(expenseTotalPrice) !==
        roundNumberTo2Decimals(expense.splits.reduce((total, curr: ExpenseSplit) => roundNumberTo2Decimals(total + curr[priceSource]), 0))
    )
}

export const syncTotalExpenseAmountsToItems = (expense: Expense, taxes: Tax[]) => {
    // total net price = sum of all net prices of items (calculates in integer as float might have information loss)
    const totalNetPrice = toAmount(expense.splits.reduce((total, curr: ExpenseSplit) => total.add(toDinero(curr.netPrice)), toDinero(0)))

    // total gross price = sum of all gross prices of items (calculates in integer as float might have information loss)
    const totalGrossPrice = toAmount(expense.splits.reduce((total, curr: ExpenseSplit) => total.add(toDinero(curr.grossPrice)), toDinero(0)))

    // if there's only one common VAT across all items, then use that one as the global VAT otherwise, set  none
    const taxRate = getCommonTaxRateAcrossItems(expense, taxes)

    // calc total tax price and gross price by using the above
    // TODO: EXPENSE REFACTOR - double check accuracy
    // const totalTaxPrice = roundNumberTo2Decimals(taxRate !== NO_TAX_RATE ? (totalNetPrice * getTax(taxes, taxRate)) / 100 : calcSumOverAllItemSplitTaxes(expense, taxes))
    const totalTaxPrice = toAmount(expense.splits.reduce((total, curr: ExpenseSplit) => total.add(toDinero(curr.taxPrice)), toDinero(0)))

    // clear up all float error in the splits.
    const splits = [...expense.splits].map((split) => ({
        ...split,
        taxPrice: toAmount(toDinero(split.taxPrice)),
        grossPrice: toAmount(toDinero(split.grossPrice)),
        netPrice: toAmount(toDinero(split.netPrice)),
    }))

    return { totalNetPrice, totalTaxPrice, totalGrossPrice, taxRate, splits }
}

const getCommonTaxRateAcrossItems = (expense: Expense, taxes: Tax[]) => {
    const onlyOneTaxRateAcrossItems = [...new Set(expense.splits.filter(({ taxRate }) => !!taxRate).map(({ taxRate }) => getTax(taxes, taxRate!)))].length === 1
    return onlyOneTaxRateAcrossItems ? expense.splits[0].taxRate : NO_TAX_RATE
}

export const calculateTotalTax = (splits: ExpenseSplit[]) => {
    const taxTotal = splits.reduce((acc, split) => acc + split.taxPrice, 0)
    return roundNumberTo2Decimals(taxTotal)
}

// When cc/cc2/ea is different for each items, then expense shouldnt have any of these info.\
// But when it's all the same, then it should be set to follow what has been filled in the items.
export const getExpenseFieldsSummaryBasedOnSplits = (splits: ExpenseSplit[], expenseTaxRate: string, syncTax: boolean = false) => {
    const isUniform = isSplitPropertiesSame(splits)

    // If tax is synced and tax are all the same, adjust the taxRate to be the split's taxRate.
    // If not, set to the expense's tax rate
    let targetTaxRate: string | undefined = expenseTaxRate
    if (isUniform.taxRate && syncTax) {
        targetTaxRate = splits[0]?.taxRate
    } else if (syncTax) {
        targetTaxRate = undefined
    }

    return {
        costCenter: isUniform.costCenter ? splits[0]?.costCenter : undefined,
        costCenter2: isUniform.costCenter2 ? splits[0]?.costCenter2 : undefined,
        expenseAccount: isUniform.expenseAccount ? splits[0]?.expenseAccount : undefined,
        taxRate: targetTaxRate === NO_TAX_RATE ? undefined : targetTaxRate,
    }
}

export const initializeExpenseItemSplitsForForm = (expenseData: any) => {
    if (expenseData.splits) {
        for (const split of expenseData.splits) {
            if (split.taxRate === undefined) split.taxRate = NO_TAX_RATE
        }
    }
}

export const deleteUnnecessaryExpenseFormData = (expenseData: any) => {
    if (expenseData.taxRate === NO_TAX_RATE) delete expenseData.taxRate
    if (expenseData.paymentOption === ExpensePaymentOptionEnum.SMART_CARD || expenseData.paymentFlowInformation === NOT_SET_VALUE) delete expenseData.paymentFlowInformation
    if (expenseData.splits)
        expenseData.splits = expenseData.splits.map((itemSplit: ItemSplit) => ({
            ...itemSplit,
            taxRate: itemSplit.taxRate === NO_TAX_RATE ? undefined : itemSplit.taxRate,
        }))
    if (expenseData.items) delete expenseData.items // TODO - EXPENSE REFACTOR: Remove this, when Expense deserializable is updated to get rid of items.
}

/**
 * if this returns true, that means the there's no duplicate, or the user clicked on "continue".
 * if this returns false, that means the there's a duplicate AND the user clicked on "back"
 */
export const checkForDuplicate = async (expense: Expense, invoiceNumber?: string, options?: { checkInvoice?: boolean; checkExpense?: boolean; ignoreIds?: string[] }) => {
    const data = await ExpenseService.checkForDuplicate({ id: expense.id, invoiceNumber: invoiceNumber || expense.invoiceNumber, ...(options ? options : {}) })
    return !data.isDuplicate || (await checkForDuplicateInvoiceNumber(data))
}

export const checkForDuplicateInvoiceFile = async (conflictingData: FileUploadDuplicateData) =>
    !conflictingData.isDuplicate || (await DialogService.confirmDuplicateInvoiceFileWarning(conflictingData))

export const checkForDuplicateInvoiceNumber = async (conflictingData: any) =>
    !conflictingData.isDuplicate || (await DialogService.confirmDuplicateInvoiceNumberWarning(conflictingData))

export const buildNewApproverUpdateData = (
    expense: Expense,
    workflows: Workflow[],
    employees: Employee[],
    costCenters: CostCenter[],
    loggedInProfile: Employee,
    isPreApprovedSubscription: boolean,
    isNew: boolean
) => {
    // re-determine approver
    const requestedBy = GetEmployeeById(employees, expense.requestedBy)
    if (requestedBy) {
        const expensePrice = loggedInProfile.settings.showGrossAmount ? expense.totalGrossPrice : expense.totalNetPrice
        let idealApprover = determineApprover(requestedBy, expensePrice, expense.currency)
        const possibleApprovers = determinePossibleApproversForExpense(expense, employees, costCenters, loggedInProfile, workflows, isPreApprovedSubscription, isNew)

        // for payment status flows, the auto approver is forced
        const autoApprover = EmployeeService.getAutoApprover()
        if (expense.paymentFlowInformation) {
            idealApprover = autoApprover.id
            if (!findAutoApproverInSet(possibleApprovers)) {
                possibleApprovers.push(autoApprover)
            }
        }

        const possibleApproverIds = possibleApprovers.map(({ id }) => id)

        // update if necessary. if ideal approver is undefined, falling back to any of the approver available in the possibleApproverIds (should be admins).
        if ((idealApprover && possibleApproverIds.includes(idealApprover)) || (!idealApprover && !possibleApproverIds.includes(expense.approvalNeededBy))) {
            return { approvalNeededBy: idealApprover ?? possibleApproverIds[0] }
        }
    }

    return {}
}

interface ExpenseUpdateData extends Pick<Expense, "costCenter" | "costCenter2" | "expenseAccount" | "paymentFlowInformation"> {}

/**
 * Applies vendor and employee rules corrding to company, user and vendor settings depending on expense vendorType
 *
 * @returns expense data that should be updated
 */
export const buildUpdateDataAccordingToSetting = (expense: Expense, vendors: Vendor[], users: Employee[], company: Company) => {
    const userId = expense?.requestedBy
    const vendorId = expense?.vendor
    const companyId = company._id
    const vendorRulesShouldOverwrite = company.vendorRulesOverwriteEmployeeRulesEnabled

    const updateData: ExpenseUpdateData = { expenseAccount: undefined }

    if (!userId && !vendorId) return updateData
    if (userId && !vendorId) return buildEmployeeRuleUpdateData(userId, users, companyId)

    // if the VendorTypeEnum is USER => only apply the employee rules (of the person requesting the expense here: userId)
    if (expense.vendorType === VendorTypeEnum.USER) {
        const employeeRules = buildEmployeeRuleUpdateData(userId!, users, companyId)

        if (isEmptyObject(employeeRules)) return updateData

        updateData.costCenter = employeeRules.costCenter ?? expense.costCenter
        updateData.costCenter2 = employeeRules.costCenter2 ?? expense.costCenter2
        updateData.expenseAccount = employeeRules.expenseAccount ?? expense.expenseAccount
        updateData.paymentFlowInformation = expense.paymentFlowInformation

        return updateData
    }

    if (!userId && vendorId) return buildVendorRuleUpdateData(expense, vendorId, vendors, company)

    const vendorRules = buildVendorRuleUpdateData(expense, vendorId!, vendors, company)
    const employeeRules = buildEmployeeRuleUpdateData(userId!, users, companyId)

    if (isEmptyObject(vendorRules) && isEmptyObject(employeeRules)) return updateData

    if (vendorRulesShouldOverwrite) {
        updateData.costCenter = vendorRules.costCenter ?? employeeRules.costCenter ?? expense.costCenter
        updateData.costCenter2 = vendorRules.costCenter2 ?? employeeRules.costCenter2 ?? expense.costCenter2
        updateData.expenseAccount = vendorRules.expenseAccount ?? employeeRules.expenseAccount ?? expense.expenseAccount
        updateData.paymentFlowInformation = vendorRules.paymentFlowInformation ?? expense.paymentFlowInformation
    } else {
        updateData.costCenter = employeeRules.costCenter ?? vendorRules.costCenter ?? expense.costCenter
        updateData.costCenter2 = employeeRules.costCenter2 ?? vendorRules.costCenter2 ?? expense.costCenter2
        updateData.expenseAccount = employeeRules.expenseAccount ?? vendorRules.expenseAccount ?? expense.expenseAccount
        updateData.paymentFlowInformation = vendorRules.paymentFlowInformation ?? expense.paymentFlowInformation
    }
    return updateData
}

const buildVendorRuleUpdateData = (expense: Expense, vendorId: string, vendors: Vendor[], company: any) => {
    const updateData: ExpenseUpdateData = { expenseAccount: undefined }
    const vendor = vendors.find((vendor: Vendor) => vendor.id === vendorId)

    if (vendor?.rule?.costCenter) updateData.costCenter = vendor?.rule?.costCenter
    if (vendor?.rule?.costCenter2) updateData.costCenter2 = vendor?.rule?.costCenter2
    if (vendor?.rule?.expenseAccount) updateData.expenseAccount = vendor?.rule?.expenseAccount
    if (vendor?.rule?.expensePaymentFlowInformation && expense.paymentOption !== ExpensePaymentOptionEnum.SMART_CARD && !expense.isReimbursement) {
        if (!isPaymentFlowInfoDisabled(vendor?.rule?.expensePaymentFlowInformation, company)) {
            updateData.paymentFlowInformation = vendor?.rule?.expensePaymentFlowInformation
        }
    }

    return updateData
}

const buildEmployeeRuleUpdateData = (userId: string, users: Employee[], companyId: string) => {
    const updateData: ExpenseUpdateData = { expenseAccount: undefined }
    const user = users.find((user: User) => user.id === userId)
    const rule = user?.rule?.find((rule: UserRule) => rule.companyId === companyId)
    if (rule?.costCenter) updateData.costCenter = rule?.costCenter
    if (rule?.costCenter2) updateData.costCenter2 = rule?.costCenter2
    if (rule?.expenseAccount) updateData.expenseAccount = rule?.expenseAccount
    return updateData
}

export const getDefaultApprover = (
    expense: any,
    employees: Employee[],
    costCenters: CostCenter[],
    workflows: Workflow[],
    loggedInProfile: Employee,
    isPreApprovedSubscription: boolean,
    isNew: boolean
) => {
    let defaultApprover
    const requestedBy = GetEmployeeById(employees, expense.requestedBy)
    const possibleApprovers = determinePossibleApproversForExpense(expense, employees, costCenters, loggedInProfile, workflows, isPreApprovedSubscription, isNew)

    const notMeOrAutoApprover = (approver: Employee) => approver?.id !== loggedInProfile.id && approver?.email !== FINWAY_ADMIN_EMAIL

    if (isPreApprovedSubscription && costCenters.length > 0 && costCenters[0].responsibleUser !== loggedInProfile.id) costCenters[0].responsibleUser
    else {
        const expensePrice = loggedInProfile.settings.showGrossAmount ? expense.totalGrossPrice : expense.totalNetPrice
        const idealApprover = determineApprover(requestedBy || loggedInProfile, expensePrice, expense.currency)
        if (idealApprover) defaultApprover = idealApprover
        else defaultApprover = possibleApprovers.length > 0 ? possibleApprovers.find(notMeOrAutoApprover)?.id : expense.approvalNeededBy
    }

    // for payment status flows, the auto approver is forced
    const autoApprover = EmployeeService.getAutoApprover()
    if (expense.paymentFlowInformation) {
        defaultApprover = autoApprover.id
        if (!findAutoApproverInSet(possibleApprovers)) {
            possibleApprovers.push(autoApprover)
        }
    }

    if (defaultApprover === loggedInProfile.id) defaultApprover = possibleApprovers.find(notMeOrAutoApprover)?.id

    return defaultApprover
}

export const getCostCenterSplitInfo = (costCenterSplits: ExpenseSplit[], expense: Expense, loggedInProfile: Employee, taxes: Tax[]) => {
    return costCenterSplits
        .map((s) => {
            let percentage = isInvoiceSplit(s, expense) ? s.percentage : 0
            let price = 0
            if (loggedInProfile.settings.showGrossAmount) {
                price = s.grossPrice
            } else {
                price = s.netPrice
            }

            return {
                percentage,
                price,
            }
        })
        .reduce(
            (total, curr) => ({
                percentage: total.percentage + curr.percentage,
                price: total.price + curr.price,
            }),
            { percentage: 0, price: 0 }
        )
}

export const dateSort = { field: "datePurchased", order: "desc" }

export const getDashboardSearchTargets = (useGross: boolean) => [
    "approverName",
    "requesterName",
    "vendorName",
    "invoiceNumber",
    "description",
    "items.name",
    "expenseNumber",
    useGross ? "totalGrossPrice" : "totalNetPrice",
]

export const isFullyProcessedSubscription = (expense: Expense) =>
    expense.kind === ExpenseKindEnum.SUBSCRIPTION &&
    expense.subscriptionType === SubscriptionTypeEnum.INITIAL_REQUEST &&
    [ExpenseStatusEnum.PAID, ExpenseStatusEnum.DONE].includes(expense.status)

export const archiveExpenses = async (expenses: Expense[], loggedInUser: User, archiveAfterXDays: number) => {
    await Promise.all(expenses.map((e: Expense) => archiveExpense(e, loggedInUser, archiveAfterXDays)))
    store.dispatch(refetchExpenseTables())
    store.dispatch(fetchDashboardExpensesCounts())
    store.dispatch(setShouldRefetchExpenses(true))
}

export const archiveExpense = (expensePara: Expense, loggedInUser: User, archiveAfterXDays: number) => {
    const expense = copyDeeply(expensePara, Expense)

    expense.isManuallyArchived = !isExpenseArchived(expense, archiveAfterXDays)

    expense.comments.push(
        new CommentItem({
            postedBy: loggedInUser.id,
            message: expense.isManuallyArchived ? `request.archived` : `request.unarchived`,
            datePosted: new Date(),
            isAutoGenerated: true,
        })
    )

    return applyExpenseUpdate(expense, expense.isManuallyArchived ? "archived" : "unarchived", false, false)
}

export const isExpenseArchived = (expense: Expense, archiveAfterXDays: number) =>
    !isNotSet(expense.isManuallyArchived)
        ? expense.isManuallyArchived
        : [ExpenseStatusEnum.DONE, ExpenseStatusEnum.DECLINED].includes(expense.status) &&
          moment(expense.datePurchased).isSameOrBefore(moment().subtract(archiveAfterXDays, "days").toDate())

export const isExpenseManuallyUnarchived = (expense: Expense, archiveAfterXDays: number) =>
    !isNotSet(expense.isManuallyArchived) && !expense.isManuallyArchived && moment(expense.datePurchased).isSameOrBefore(moment().subtract(archiveAfterXDays, "days").toDate())

export const isSplitPropertiesSame = (splits: ExpenseSplit[]) => ({
    costCenter: splits.every((s) => s.costCenter === splits[0].costCenter),
    costCenter2: splits.every((s) => s.costCenter2 === splits[0].costCenter2),
    expenseAccount: splits.every((s) => s.expenseAccount === splits[0].expenseAccount),
    taxRate: splits.every((s) => s.taxRate === splits[0].taxRate),
})

export const calculateSplitPercentageTotal = (splits: InvoiceSplit[]) => splits.reduce((accu: number, split: InvoiceSplit) => accu + Number(split.percentage), 0)

/**
 * Type guard to determine if the splits of an expense are InvoiceSplit. If true, typescript can safely assume in the rest of the code block that the split is InvoiceSplit
 */
export const areInvoiceSplits = (splits: ExpenseSplit[], expense: Expense): splits is InvoiceSplit[] => expense.splitType === SplitTypeEnum.SPLIT
export const isInvoiceSplit = (split: ExpenseSplit, expense: Expense): split is InvoiceSplit => expense.splitType === SplitTypeEnum.SPLIT

/**
 * Type guard to determine if the splits of an expense are ItemSplit. If true, typescript can safely assume in the rest of the code block that the split is ItemSplit
 */
export const areItemSplits = (splits: ExpenseSplit[], expense: Expense): splits is ItemSplit[] => expense.splitType === SplitTypeEnum.ITEM
export const isItemSplit = (split: ExpenseSplit, expense: Expense): split is ItemSplit => expense.splitType === SplitTypeEnum.ITEM

export const isHospitalityExpense = (expense: Expense): expense is Hospitality => expense.kind === ExpenseKindEnum.HOSPITALITY
export const isPerDiemExpense = (expense: Expense): expense is PerDiem => expense.kind === ExpenseKindEnum.PER_DIEM
export const isMileageExpense = (expense: Expense): expense is Mileage => expense.kind === ExpenseKindEnum.MILEAGE
export const isMileageOrPerDiem = (expense: Expense): expense is Mileage | PerDiem => [ExpenseKindEnum.MILEAGE, ExpenseKindEnum.PER_DIEM].includes(expense.kind)

export const getExpenseModelByKind = (kind: ExpenseKindEnum) => {
    switch (kind) {
        case ExpenseKindEnum.HOSPITALITY:
            return Hospitality
        case ExpenseKindEnum.PER_DIEM:
            return PerDiem
        case ExpenseKindEnum.MILEAGE:
            return Mileage
        case ExpenseKindEnum.ONE_TIME_EXPENSE:
        case ExpenseKindEnum.SUBSCRIPTION:
        case ExpenseKindEnum.RECURRING_SUBSCRIPTION:
        default:
            return Expense
    }
}

export const isExpenseAlreadyPaid = (expense: Expense) => !isNotSet(expense.paymentFlowInformation) || [ExpenseStatusEnum.PAID, ExpenseStatusEnum.DONE].includes(expense.status)

/**
 * Mutates splits array so all the values of the selected property is changed to the new value
 */
export const changeAllSplitPropertyValues = (splits: ExpenseSplit[], propertyToChange: "costCenter" | "costCenter2" | "expenseAccount", newValue: string) => {
    for (const split of splits) {
        split[propertyToChange] = newValue
    }
}

/**
 * @returns true if the expense could move past the APPROVED stage until the end.
 */
export const isExpenseAbleToProceed = (data: Expense, creditor?: CreditorInterface) => {
    // Invoice data must be complete
    const isInvoiceDataComplete = Boolean(data.invoiceNumber && data.invoiceDate)

    // Cost center must be completely populated
    const isCostCenterComplete = data.splits?.length === 0 || data.splits?.every((s) => s.costCenter)

    // Creditor number must exist on APPROVED stage
    const isCreditorNumberOk = isExpenseCreditorNumberInfoOk(data, creditor)

    const itHasInvoicesIfInPaidStatus = expenseHasInvoiceInPaidStatus(data)

    return isInvoiceDataComplete && isCostCenterComplete && isCreditorNumberOk && itHasInvoicesIfInPaidStatus
}

export const isExpenseCreditorNumberInfoOk = (expense: Pick<Expense, "status">, creditor?: CreditorInterface) => {
    return expense.status <= ExpenseStatusEnum.APPROVED && Boolean(creditor?.creditorNumber)
}

export const expenseHasInvoiceInPaidStatus = (expense: Expense) => {
    return expense.status !== ExpenseStatusEnum.PAID || expense.invoices.length > 0
}

export const isExpenseSwitchingShown = (expense: Expense, archiveAfterXDays: number) => {
    return !isExpenseArchived(expense, archiveAfterXDays)
}

export const getTableAndAdjacentExpenseLabel = (status: ExpenseStatusEnum, requestedBy: string): [TablesEnum, string] => {
    const loggedInUser = UserService.getLoggedInEmployeeProfile()
    const isRequestedByLoggedInUser = loggedInUser.id == requestedBy
    const isAdmin = loggedInUser.activeCompanyProfile.isAdmin
    const activeTab = useActiveTab()

    if (activeTab === TabTypeEnum.ALL) {
        return [TablesEnum.ALL_REQUESTS, "all"]
    }

    switch (status) {
        case ExpenseStatusEnum.PURCHASE_PENDING:
            return isRequestedByLoggedInUser ? [TablesEnum.TODO_APPROVAL_PENDING, "approval_pending"] : [TablesEnum.TODO_PURCHASE_APPROVAL, "purchase_approval_needed"]
        case ExpenseStatusEnum.DOCS_NEEDED:
            return [TablesEnum.TODO_DOCS_NEEDED, "docs_needed"]
        case ExpenseStatusEnum.INVOICE_PENDING:
            return isRequestedByLoggedInUser ? [TablesEnum.TODO_APPROVAL_PENDING, "approval_pending"] : [TablesEnum.TODO_INVOICE_APPROVAL, "invoice_approval_needed"]
        case ExpenseStatusEnum.APPROVED:
            return isAdmin ? [TablesEnum.REVIEW_REQUESTS, "in_review"] : [TablesEnum.IN_PROGRESS, "in_progress"]
        case ExpenseStatusEnum.REVIEWED:
            return isAdmin ? [TablesEnum.PAY_AND_EXPORT_TO_BE_PAID, "reviewed"] : [TablesEnum.IN_PROGRESS, "in_progress"]
        case ExpenseStatusEnum.PAID:
            return isAdmin ? [TablesEnum.PAY_AND_EXPORT_TO_BE_EXPORTED, "paid"] : [TablesEnum.IN_PROGRESS, "in_progress"]
        case ExpenseStatusEnum.DONE:
        case ExpenseStatusEnum.DECLINED:
            return [TablesEnum.DONE_REQUESTS, "done"]
        default:
            return [TablesEnum.TODO_PURCHASE_APPROVAL, ""]
    }
}

export const shouldExpenseIncludeVendors = (expense: Expense) => !isMileageOrPerDiem(expense)

export const shouldExpenseIncludeEmployeesAsVendor = (expense: Expense) => expense.isReimbursement

export const determineDefaultVendorForReimbursement = (requestedBy: string, expenseKind: ExpenseKindEnum, creditors: CreditorInterface[]) => {
    if (![ExpenseKindEnum.PER_DIEM, ExpenseKindEnum.MILEAGE].includes(expenseKind)) return undefined

    return creditors.find((c) => c.type === VendorTypeEnum.USER && c.id === requestedBy)
}

export const isExpenseReviewDisabled = (expenseData: any) =>
    expenseData.status === ExpenseStatusEnum.APPROVED &&
    expenseData.paymentFlowInformation !== ExpensePaymentFlowInformationEnum.REPORTING_ONLY &&
    (!expenseData.invoiceNumber || !expenseData.invoiceDate || !expenseData.invoices.length)

export const findAutoApproverInSet = (employees: Employee[]) => employees.find((approver) => approver.email === FINWAY_ADMIN_EMAIL)

export const isPaymentFlowInfoDisabled = (paymentFlowInformation: ExpensePaymentFlowInformationEnum, company: Company) => {
    if (paymentFlowInformation === ExpensePaymentFlowInformationEnum.ALREADY_PAID && !company.expensePaymentFlowInformation?.isAlreadyPaidEnabled) {
        // already paid disabled
        return true
    }

    if (paymentFlowInformation === ExpensePaymentFlowInformationEnum.ALREADY_APPROVED_AND_PAID && !company.expensePaymentFlowInformation?.isAlreadyApprovedAndPaidEnabled) {
        // already approved & paid disabled
        return true
    }

    if (paymentFlowInformation === ExpensePaymentFlowInformationEnum.REPORTING_ONLY && !company.expensePaymentFlowInformation?.isReportingOnlyEnabled) {
        // reporting only disabled
        return true
    }

    return false
}

export const isPaymentOngoing = (isInPaymentProcess: boolean, payment?: FinApiPayment) => payment && (payment.status === FinApiPaymentStatusEnum.PENDING || isInPaymentProcess)

export const doesExpenseHaveDatevIncompliantBuCodes = (expense: Expense, taxValues: any[]) => {
    const usedTaxValues = [expense.taxRate, expense.splits.map((split) => split?.taxRate)].map((taxRateId) => taxValues.find((tax) => tax._id === taxRateId))
    return usedTaxValues.some((taxValue) => taxValue?.buCode && !isBuCodeDATEVCompliant(taxValue?.buCode))
}

export const showPaymentFailedAlert = (status: ExpenseStatusEnum, payment?: FinApiPayment) =>
    payment &&
    [FinApiPaymentStatusEnum.DISCARDED, FinApiPaymentStatusEnum.NOT_SUCCESSFUL].includes(payment.status) &&
    ![ExpenseStatusEnum.PAID, ExpenseStatusEnum.DONE].includes(status)

export const generatePerDiemDestinationTree = (perDiemDestinations: PerDiemDestination[]): PerDiemDestination[] => {
    // add children
    const treeDestinations: PerDiemDestination[] = []
    for (const destination of perDiemDestinations) {
        if (destination.parent) continue // if have a parent, skip...

        if (destination.children && destination.children.length > 0) {
            const childrenDestinations: PerDiemDestination[] = perDiemDestinations.filter((resource) => resource.parent === destination._id)
            destination.children = childrenDestinations
        }
        treeDestinations.push(destination)
    }
    return treeDestinations
}

export const getDefaultInvoiceNumber = (kind: ExpenseKindEnum, nextExpenseNumber?: number) => {
    if (!nextExpenseNumber) return undefined
    switch (kind) {
        case ExpenseKindEnum.PER_DIEM:
            return `finway-pd-${moment().get("year")}-${nextExpenseNumber}`
        case ExpenseKindEnum.MILEAGE:
            return `finway-ml-${moment().get("year")}-${nextExpenseNumber}`
        default:
            return undefined
    }
}

export const getStatusToResetTo = (expense: Expense) => {
    // -2 for reviewed since we got to jump over the rejected status)
    const defaultNewStatus = expense.status - (expense.status === ExpenseStatusEnum.REVIEWED ? 2 : 1)

    switch (expense.paymentFlowInformation) {
        case ExpensePaymentFlowInformationEnum.ALREADY_APPROVED_AND_PAID:
            return expense.status === ExpenseStatusEnum.DONE ? ExpenseStatusEnum.PAID : ExpenseStatusEnum.APPROVED
        case ExpensePaymentFlowInformationEnum.ALREADY_PAID:
            return expense.status === ExpenseStatusEnum.PAID ? ExpenseStatusEnum.APPROVED : defaultNewStatus
        case ExpensePaymentFlowInformationEnum.REPORTING_ONLY:
            return ExpenseStatusEnum.APPROVED
        default:
            return defaultNewStatus
    }
}

export const isCreditorNumberMissing = (expense: Expense, vendor: CreditorInterface, isAdmin: boolean) =>
    expense.status === ExpenseStatusEnum.APPROVED && vendor?.type === VendorTypeEnum.USER && !vendor.creditorNumber && isAdmin

export const toDinero = (amount: number) => dinero({ amount: Math.round(amount * 100) })

export const toAmount = (dineroInstance: dinero.Dinero) => dineroInstance.getAmount() / 100

export const detectSplitRoundingError = (splits: ExpenseSplit[]) => {
    const expectedAmount = {
        net: 0,
        tax: 0,
        gross: 0,
    }
    const roundedAmount = {
        net: 0,
        tax: 0,
        gross: 0,
    }
    for (const split of splits) {
        expectedAmount.net += split.netPrice
        roundedAmount.net += roundNumberTo2Decimals(split.netPrice)

        expectedAmount.tax += split.taxPrice
        roundedAmount.tax += roundNumberTo2Decimals(split.taxPrice)

        expectedAmount.gross += split.grossPrice
        roundedAmount.gross += roundNumberTo2Decimals(split.grossPrice)
    }

    return {
        netPrice: roundNumberTo2Decimals(expectedAmount.net - roundedAmount.net),
        grossPrice: roundNumberTo2Decimals(expectedAmount.gross - roundedAmount.gross),
        taxPrice: roundNumberTo2Decimals(expectedAmount.gross - roundedAmount.gross),
    }
}

/**
 * Calculates if there is an error in the amounts vertically (net / gross / tax).
 * returns the difference between: (Split 0's price + ... + Split n's price) and (Expense price)
 * 0 in tax/gross/net means the amounts is fine.
 */
export const detectVerticalSplitError = (expense: Expense, splits: ExpenseSplit[]) => {
    const splitAmount = {
        net: toDinero(0),
        tax: toDinero(0),
        gross: toDinero(0),
    }

    for (const split of splits) {
        splitAmount.net = splitAmount.net.add(toDinero(split.netPrice))
        splitAmount.tax = splitAmount.tax.add(toDinero(split.taxPrice))
        splitAmount.gross = splitAmount.gross.add(toDinero(split.grossPrice))
    }

    splitAmount.net = splitAmount.net.subtract(toDinero(expense.totalNetPrice))
    splitAmount.tax = splitAmount.tax.subtract(toDinero(expense.totalTaxPrice))
    splitAmount.gross = splitAmount.gross.subtract(toDinero(expense.totalGrossPrice))

    return {
        netPrice: toAmount(splitAmount.net),
        taxPrice: toAmount(splitAmount.tax),
        grossPrice: toAmount(splitAmount.gross),
    }
}

/**
 * Calculates if there is an error horizontally
 * Returns array of indices of rows that doesnt add up
 * Split 0's net + tax should equal gross
 */
export const detectHorizontalSplitError = (splits: ExpenseSplit[]) => {
    let errors: number[] = []
    splits.forEach((split, index) => {
        const { netPrice, taxPrice, grossPrice } = split
        if (!toDinero(netPrice).add(toDinero(taxPrice)).equalsTo(toDinero(grossPrice))) errors.push(index)
    })

    return errors
}

export const convertAmountToInteger = (amt: number) => Math.round(amt * 100)

export const adjustGrossAmountsIfNeeded = (splits: ExpenseSplit[], expense: Expense) => {
    if (!areInvoiceSplits(splits, expense)) {
        return splits
    }

    const roundingError = detectSplitRoundingError(splits)
    if (roundingError.grossPrice !== 0) {
        const totalGross = splits.reduce((acc, val) => acc + val.grossPrice, 0)
        const percentages = splits.map((s) => Number(s.percentage))
        const grossPriceAllocation = dinero({ amount: convertAmountToInteger(totalGross) }).allocate(percentages)
        return [...splits].map((s, i) => {
            return {
                ...s,
                grossPrice: grossPriceAllocation[i].getAmount() / 100,
            }
        })
    }

    return splits
}

export const roundSplitAmounts = (splits: ExpenseSplit[]): ExpenseSplit[] => {
    return splits.map((split) => ({
        ...split,
        netPrice: toAmount(toDinero(split.netPrice)),
        grossPrice: toAmount(toDinero(split.grossPrice)),
        taxPrice: toAmount(toDinero(split.taxPrice)),
    }))
}
export const isMatched = (expense: Expense) => summarizeExpenseTransactions(expense) !== undefined

export const addInvoiceCommentsIfNeeded = (expense: Expense, previousExpense: Expense, user: User) => {
    const newExpenseUrls = expense.invoices.map(({ url }: InvoiceInterface) => url)
    const previousExpenseUrls = previousExpense.invoices.map(({ url }: InvoiceInterface) => url)

    const newInvoices = expense.invoices.filter(({ url }: InvoiceInterface) => !previousExpenseUrls.includes(url))
    const removedInvoices = previousExpense.invoices.filter(({ url }: InvoiceInterface) => !newExpenseUrls.includes(url))

    const commentMessages = [
        ...removedInvoices.map(({ fileName }: InvoiceInterface) =>
            fileName ? `${AutoGeneratedCommentsEnum.INVOICE_DELETED_WITH_FILENAME},${fileName}` : AutoGeneratedCommentsEnum.INVOICE_DELETED
        ),
        ...newInvoices.map(({ fileName }: InvoiceInterface) =>
            fileName ? `${AutoGeneratedCommentsEnum.INVOICE_UPLOADED_WITH_FILENAME},${fileName}` : AutoGeneratedCommentsEnum.INVOICE_UPLOADED
        ),
    ]
    expense.comments.push(
        ...commentMessages.map(
            (message: string) =>
                new CommentItem({
                    postedBy: user.id,
                    message,
                    datePosted: new Date(),
                    isAutoGenerated: true,
                })
        )
    )
}

export const getReimbursementLabel = (kind: ExpenseKindEnum) => {
    switch (kind) {
        case ExpenseKindEnum.HOSPITALITY:
            return "hospitality"
        case ExpenseKindEnum.PER_DIEM:
            return "per_diem"
        case ExpenseKindEnum.MILEAGE:
            return "mileage"
        case ExpenseKindEnum.STANDARD:
            return "standard"
        default:
            return "standard_reimbursement"
    }
}

// A product requirement for export filter in ALL tab
export const getFilteredExpenseStatusArrayForExport = (exportFormat: ExportFormatEnum) => {
    switch (exportFormat) {
        case ExportFormatEnum.DATEV:
            return [ExpenseStatusEnum.REVIEWED, ExpenseStatusEnum.PAID, ExpenseStatusEnum.DONE]
        case ExportFormatEnum.ALL:
        case ExportFormatEnum.CSV:
            return [
                ExpenseStatusEnum.PURCHASE_PENDING,
                ExpenseStatusEnum.DOCS_NEEDED,
                ExpenseStatusEnum.INVOICE_PENDING,
                ExpenseStatusEnum.APPROVED,
                ExpenseStatusEnum.REVIEWED,
                ExpenseStatusEnum.PAID,
                ExpenseStatusEnum.DONE,
                ExpenseStatusEnum.DECLINED,
            ]
        case ExportFormatEnum.INVOICES:
            return [ExpenseStatusEnum.INVOICE_PENDING, ExpenseStatusEnum.APPROVED, ExpenseStatusEnum.REVIEWED, ExpenseStatusEnum.PAID, ExpenseStatusEnum.DONE]
        default:
            return []
    }
}

// if expense format is different than INVOICES or ALL (they don't advance status by default) and the expense status is or includes PAID then we let the user decide if they want to advance expense status to DONE
export const shouldAskToAdvanceStatusOnExport = (format: ExportFormatEnum, status: any) =>
    ![ExportFormatEnum.INVOICES, ExportFormatEnum.ALL].includes(format) && (!status || Number(status) === ExpenseStatusEnum.PAID)

export const addUpdateCommentToExpense = (expense: Expense, loggedInUser: User) => {
    expense.comments.unshift(
        new CommentItem({
            postedBy: loggedInUser.id,
            message: AutoGeneratedCommentsEnum.REQUEST_UPDATED,
            datePosted: new Date(),
            isAutoGenerated: true,
        })
    )
}

export const sumTotalGrossPriceByCurrencies = (expenses: Expense[]) => {
    const totalGrossPriceByCurrency = new Map<CurrencyEnum, number>()

    for (const expense of expenses) {
        const currentTotalPrice = totalGrossPriceByCurrency.get(expense.currency)

        if (currentTotalPrice) {
            totalGrossPriceByCurrency.set(expense.currency, currentTotalPrice + expense.totalGrossPrice)
        } else {
            totalGrossPriceByCurrency.set(expense.currency, expense.totalGrossPrice)
        }
    }

    return totalGrossPriceByCurrency
}

export const exportSepa = async (expenses: Expense[], subjectLines: any) => {
    const response = await FileService.export(`&_id[in]=${expenses.map((exp) => exp.id).join(",")}&format=${ExportFormatEnum.SEPA}`, false, subjectLines)
    const isAsyncExport = response?.status === httpStatus.ACCEPTED

    if (isAsyncExport) {
        const filteredOutExpenseNumbers = response?.data.filteredOutExpenseNumbers
        if (filteredOutExpenseNumbers?.length) {
            NotificationService.send(
                NotificationTypeEnum.WARNING,
                i18n.t(`notification:request.async_export_started_partial.title`),
                i18n.t(`notification:request.async_export_started_partial.message`, { expenses: filteredOutExpenseNumbers.join(", ") }),
                0
            )
        } else {
            NotificationService.send(NotificationTypeEnum.INFO, i18n.t(`notification:async_export_started.title`), i18n.t(`notification:async_export_started.message`), 0)
        }
    } else {
        const { base64File, expenses } = parseMultipartBody(response)
        const file = getBufferFromEncoding(base64File, "base64")
        if (expenses?.filteredOutExpenseNumbers?.length) {
            NotificationService.send(
                NotificationTypeEnum.WARNING,
                i18n.t(`notification:request.exported_bulk_partial.title`),
                i18n.t(`notification:request.exported_bulk_partial.message`, { expenses: expenses.filteredOutExpenseNumbers.join(", ") }),
                0
            )
        }
        FileDownload(file, "sepa_expense.zip")
    }
}
